import { store } from "src/app/store";
import { logout } from "src/features/authSlice";
import * as Sentry from "@sentry/react";
import { runtimeConfig } from "./runtimeConfig";

export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

interface CallApiOptions {
  endpoint: string;
  method: HttpMethod;
  body?: any;
  redirect?: boolean;
  contentType?: string;
  file?: File;
  overrideLoginId?: string;
  allowedErrorStatus?: number[];
  customHeaders?: Record<string, string>;
  baseUrl?: string;
  /** Control credentials mode. Defaults to 'include'. Use 'omit' for cross-origin APIs that use AllowAnyOrigin. */
  credentials?: RequestCredentials;
}

// I created this wrapper so we could have an alternative cleaner
// way to callApi with an options object when we need to use
// optional parameters - JH 2025-02-06
export async function callApi(
  endpoint: string,
  method: HttpMethod,
  body?: any,
  redirect: boolean = true,
  contentType?: string,
  file?: File,
  overrideLoginId?: string,
  allowedErrorStatus?: number[],
): Promise<any> {
  return callApiWithOptions({
    endpoint,
    method,
    body,
    redirect,
    contentType,
    file,
    overrideLoginId,
    allowedErrorStatus,
  });
}

export async function callApiWithOptions(
  options: CallApiOptions,
): Promise<any> {
  const baseUrl = options.baseUrl ?? runtimeConfig.apiUrl;

  const state = store.getState();
  const { acctId, loginId: stateLoginId, csrfToken } = state.auth;
  const loginId = options.overrideLoginId ?? stateLoginId;
  let requestOptions: RequestInit;

  if (options.file) {
    // Handle file upload
    const formData = new FormData();
    formData.append("file", options.file); // Append file

    if (options.body) {
      Object.keys(options.body).forEach((key) => {
        formData.append(key, options.body[key]);
      });
    }

    formData.append("acctId", acctId ?? "");
    formData.append("loginId", loginId ?? "");
    formData.append("csrfToken", csrfToken ?? "");

    requestOptions = {
      method: options.method,
      body: formData,
      credentials: options.credentials ?? "include",
    };
  } else {
    // Handle regular JSON payload
    const headers = new Headers();
    headers.append("Content-Type", options.contentType ?? "application/json");
    headers.append("acctId", acctId ?? "");
    headers.append("loginId", loginId ?? "");
    headers.append("csrfToken", csrfToken ?? "");

    // Add custom headers if provided
    if (options.customHeaders) {
      Object.entries(options.customHeaders).forEach(([key, value]) => {
        headers.append(key, value);
      });
    }

    // AK: This will reload the user's record from the database or else it always returns the cached record
    // This is used in the VerifyEmail page to ensure the user's record is up to date
    if (options.endpoint.toLowerCase() === "users/isauthenticated?data=true") {
      headers.append("x-force-reload", "true");
    }

    requestOptions = {
      method: options.method,
      headers: headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: options.credentials ?? "include",
    };
  }

  Sentry.addBreadcrumb({
    category: "fetch",
    message: `Fetching data from ${baseUrl}/${options.endpoint}. Using ${requestOptions}`,
  });

  const response = await fetch(
    `${baseUrl}/${options.endpoint}`,
    requestOptions,
  );

  if (!response.ok) {
    // Check if this status code is allowed
    if (options.allowedErrorStatus?.includes(response.status)) {
      return response;
    }

    // If user is unauthorized, redirect to login page
    if (response.status === 401 && options.redirect) {
      // Make sure redux store knows we are logged out
      // Doing this clears all session storage and cookies
      store.dispatch(logout());
      console.log("Session is not authorized");
      // Carry the current page as returnUrl so the user resumes where they
      // were after re-login. Without this the session-expiry path lost the
      // user's place, which is what the now-removed lastLocation breadcrumb
      // was patching over. classifyReturnUrl rejects a /login returnUrl.
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      // Note the redirect will unload the page so nothing below this run
      window.location.href = `/login?reason=notauthorized&returnUrl=${returnUrl}`;
      return;
    } else {
      const errorBody = await response.json().catch(() => ({}));
      const error = new Error(
        errorBody.message || response.statusText || "An error occurred",
      );
      (error as any).status = response.status;
      (error as any).data = errorBody;
      throw error;
    }
  }

  return response.json();
}

// Function to handle streaming API responses
export async function callStreamingApi(
  endpoint: string,
  method: HttpMethod,
  body?: any,
  onChunk?: (chunk: any) => void,
  onComplete?: (data: any) => void,
  onError?: (error: any) => void,
  customHeaders?: Record<string, string>,
): Promise<any> {
  const baseUrl = runtimeConfig.apiUrl;

  const state = store.getState();
  const { acctId, loginId, csrfToken } = state.auth;

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("acctId", acctId ?? "");
  headers.append("loginId", loginId ?? "");
  headers.append("csrfToken", csrfToken ?? "");

  // Add custom headers if provided
  if (customHeaders) {
    Object.entries(customHeaders).forEach(([key, value]) => {
      headers.append(key, value);
    });
  }

  const requestOptions: RequestInit = {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  };

  try {
    const response = await fetch(`${baseUrl}/${endpoint}`, requestOptions);

    if (!response.ok) {
      // If user is unauthorized, redirect to login page
      if (response.status === 401) {
        store.dispatch(logout());
        console.log("Session is not authorized");
        // Carry the current page as returnUrl so the user resumes where they
        // were after re-login (see callApi above for rationale).
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/login?reason=notauthorized&returnUrl=${returnUrl}`;
        return;
      } else {
        const errorBody = await response.json().catch(() => ({}));
        const error = new Error(
          errorBody.message || response.statusText || "An error occurred",
        );
        (error as any).status = response.status;
        (error as any).data = errorBody;
        throw error;
      }
    }

    // Check if response is streaming
    const contentType = response.headers.get("content-type");
    console.log("Streaming response content-type:", contentType);

    if (contentType && contentType.includes("text/plain")) {
      // Handle plain text streaming
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let accumulatedText = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          console.log("Received chunk:", chunk);

          // Filter out the raw object names and try to extract actual content
          const filteredChunk = filterObjectNames(chunk);

          if (filteredChunk) {
            accumulatedText += filteredChunk;

            // Send each filtered chunk as it arrives
            if (onChunk) {
              onChunk({ text: filteredChunk });
            }
          }
        }

        console.log("Final accumulated text:", accumulatedText);
        if (onComplete) onComplete({ success: true });
      }
    } else if (contentType && contentType.includes("text/event-stream")) {
      // Handle Server-Sent Events (SSE) streaming (keeping for backward compatibility)
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (onChunk) onChunk(data);
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }

        if (onComplete) onComplete({ success: true });
      }
    } else {
      // Handle regular JSON response
      const data = await response.json();
      if (onComplete) onComplete(data);
      return data;
    }
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
}

// Function to filter out raw object names and extract actual text content
function filterObjectNames(chunk: string): string | null {
  console.log("Filtering chunk:", chunk);

  // Remove the raw object name that's being streamed
  const objectNamePattern = /OpenAI\.Chat\.StreamingChatCompletionUpdate/g;
  const cleanedChunk = chunk.replace(objectNamePattern, "");

  console.log("Cleaned chunk:", cleanedChunk);

  // If the chunk is empty after removing object names, return null
  if (!cleanedChunk.trim()) {
    console.log("Chunk is empty after cleaning, returning null");
    return null;
  }

  // Try to extract JSON content if it exists
  try {
    // Look for JSON-like content in the chunk
    const jsonMatch = cleanedChunk.match(/\{.*\}/);
    if (jsonMatch) {
      console.log("Found JSON match:", jsonMatch[0]);
      const jsonContent = JSON.parse(jsonMatch[0]);
      console.log("Parsed JSON content:", jsonContent);

      // Extract text content from common OpenAI response fields
      if (
        jsonContent.choices &&
        jsonContent.choices[0] &&
        jsonContent.choices[0].delta
      ) {
        const content = jsonContent.choices[0].delta.content || "";
        console.log("Extracted content from choices.delta:", content);
        return content;
      }
      if (jsonContent.content) {
        console.log(
          "Extracted content from content field:",
          jsonContent.content,
        );
        return jsonContent.content;
      }
    }
  } catch (e) {
    console.log("JSON parsing failed:", e);
    // If JSON parsing fails, return the cleaned chunk as-is
  }

  console.log("Returning cleaned chunk as-is:", cleanedChunk);
  return cleanedChunk;
}
